import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { updateModuleProgress } from '../../../SkillsApi/SkillsApi'
import './AiModuleFour.css'

const MODULE_ID = 4
const SECTION = 'AiSection'

// TODO: swap for the actual AI Module 4 video URL
const VIDEO_URL = 'https://res.cloudinary.com/dk50cmtps/video/upload/v1780034028/drone_yzbva8.mp4'

// TODO: swap these for questions that match AI Module 4's actual video content —
// these are placeholders on general AI/ML topics, same Tanglish style as Module 1-3.
const ASSESSMENT_QUESTIONS = [
  {
    id: 'q1',
    question: 'AI model deployment-ல "inference"-ஐ எப்படி define பண்றாங்க?',
    options: [
      { key: 'a', label: 'Using a trained model to make predictions on new data' },
      { key: 'b', label: 'Collecting raw training data' },
      { key: 'c', label: 'Labeling the dataset manually' },
      { key: 'd', label: 'Backing up the model weights' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q2',
    question: '"Bias" AI model-ல எப்போது ஏற்படுது?',
    options: [
      { key: 'a', label: 'When training data is unrepresentative or skewed' },
      { key: 'b', label: 'When GPU memory is full' },
      { key: 'c', label: 'When internet speed is slow' },
      { key: 'd', label: 'When the model file is too large' },
    ],
    correctKey: 'a',
  },
  {
    id: 'q3',
    question: 'Model "quantization" எதுக்காக பண்றாங்க?',
    options: [
      { key: 'a', label: 'Reducing model size and speeding up inference' },
      { key: 'b', label: 'Increasing training dataset size' },
      { key: 'c', label: 'Encrypting API keys' },
      { key: 'd', label: 'Adding more layers to the network' },
    ],
    correctKey: 'a',
  },
]

const AiModuleFour = () => {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})
  const [watchPercent, setWatchPercent] = useState(0)
  const [videoEnded, setVideoEnded] = useState(false)
  const [finishing, setFinishing] = useState(false)

  const lastSentMilestoneRef = useRef(0)

  useEffect(() => {
    document.querySelector('#root')?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Same right-click / DevTools-shortcut deterrent as AI Module 1-3.
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault()
      toast.warn('Right-click is disabled on this page.')
    }

    const handleKeyDown = (e) => {
      const isDevToolsShortcut =
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && ['I', 'J', 'C', 'i', 'j', 'c'].includes(e.key)) ||
        (e.ctrlKey && ['U', 'u'].includes(e.key))

      if (isDevToolsShortcut) {
        e.preventDefault()
        toast.warn('Inspect is disabled on this page.')
      }
    }

    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const sendMilestone = async (milestone) => {
    if (milestone <= lastSentMilestoneRef.current) return
    lastSentMilestoneRef.current = milestone
    await updateModuleProgress(SECTION, MODULE_ID, milestone)
  }

  // Milestones capped at 90 during playback — the final 100% is only sent
  // once the assessment is finished too (see handleFinish), so AI Module 5
  // only unlocks once BOTH the video and the quiz are done.
  const handleTimeUpdate = (e) => {
    const { currentTime, duration } = e.target
    if (!duration) return

    const percent = Math.min(100, Math.floor((currentTime / duration) * 100))
    setWatchPercent((prev) => (percent > prev ? percent : prev))

    const crossedMilestone = Math.min(90, Math.floor(percent / 10) * 10)
    if (crossedMilestone > lastSentMilestoneRef.current) {
      sendMilestone(crossedMilestone)
    }
  }

  const handleVideoEnded = () => {
    setWatchPercent(100)
    setVideoEnded(true)
    toast.info('Video watched! Now complete the assessment below.')
  }

  const handleAnswerSelect = (questionId, optionKey, correctKey) => {
    if (answers[questionId]) return

    setAnswers((prev) => ({ ...prev, [questionId]: optionKey }))

    if (optionKey === correctKey) {
      toast.success('Correct answer!')
    } else {
      toast.error('Wrong answer!')
    }
  }

  const allQuestionsAnswered = ASSESSMENT_QUESTIONS.every((q) => answers[q.id])
  const canFinish = videoEnded && allQuestionsAnswered

  const handleFinish = async () => {
    if (!canFinish || finishing) return
    setFinishing(true)
    await sendMilestone(100)
    toast.success('Module 4 complete — Module 5 unlocked!')
    navigate('/ai_course')
  }

  return (
    <div className="AiModuleFour_wrapper">
      <ToastContainer position="top-right" autoClose={2000} />

      <div className="AiModuleFour_content">
        <h2 className="AiModuleFour_title">Module 4: Deep Dive</h2>
        <p className="AiModuleFour_subtitle">
          Watch the full video, then complete the short assessment to unlock Module 5.
        </p>

        <div className="AiModuleFour_video_wrap">
          <video
            className="AiModuleFour_video"
            src={VIDEO_URL}
            controls
            controlsList="nodownload"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnded}
          />
        </div>

        <div className="AiModuleFour_progress_row">
          <div className="AiModuleFour_progress_track">
            <div
              className="AiModuleFour_progress_fill"
              style={{ width: `${watchPercent}%` }}
            />
          </div>
          <span className="AiModuleFour_progress_label">{watchPercent}% watched</span>
        </div>

        <div className="AiModuleFour_assessment">
          <h3 className="AiModuleFour_assessment_title">Module Assessment</h3>
          <p className="AiModuleFour_assessment_sub">
            {videoEnded
              ? `Answer all ${ASSESSMENT_QUESTIONS.length} questions to complete this module.`
              : 'Finish watching the video above to unlock the assessment.'}
          </p>

          {ASSESSMENT_QUESTIONS.map((q, qIndex) => {
            const selected = answers[q.id]

            return (
              <div className="AiModuleFour_question_card" key={q.id}>
                <p className="AiModuleFour_question_text">
                  <span className="AiModuleFour_question_num">Q{qIndex + 1}.</span> {q.question}
                </p>

                <div className="AiModuleFour_options_grid">
                  {q.options.map((opt) => {
                    const isSelected = selected === opt.key
                    const isCorrectOpt = opt.key === q.correctKey
                    let stateClass = ''

                    if (selected) {
                      if (isSelected && isCorrectOpt) stateClass = 'AiModuleFour_option_correct'
                      else if (isSelected && !isCorrectOpt) stateClass = 'AiModuleFour_option_wrong'
                      else if (!isSelected && isCorrectOpt) stateClass = 'AiModuleFour_option_reveal'
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={`AiModuleFour_option_btn ${stateClass}`}
                        onClick={() => handleAnswerSelect(q.id, opt.key, q.correctKey)}
                        disabled={!videoEnded || !!selected}
                      >
                        <span className="AiModuleFour_option_key">{opt.key.toUpperCase()}</span>
                        <span className="AiModuleFour_option_label">{opt.label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            className="AiModuleFour_complete_btn"
            onClick={handleFinish}
            disabled={!canFinish || finishing}
          >
            {finishing ? 'Saving...' : (
              <>
                Complete Module <span className="AiModuleFour_complete_check">&#10003;</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AiModuleFour